import { useState } from "react";
import { Button, Form, FormGroup, Input, Label } from "reactstrap";
import Select from "react-select";
import styles from "./InvoicesComponents.module.scss";
import DatePicker from "react-datepicker";
import axios from "axios";
import { jsPDF } from "jspdf";
import {
  BarChart,
  Bar,
  Rectangle,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from "recharts";
import moment from "moment";
import DailySummaryTable from "./PaymentNoticeComponents/DailySummaryTable";
import DischargeFormComponent from "./PaymentNoticeComponents/DischargeFormComponent";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useEffect } from "react";
import { fetchBuildings } from "../../../../actions/building.js";

export default function PaymentNotice() {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchBuildings());
  }, [dispatch]);
  
  const buildingStore = useSelector((state) => state.buildings);
  const buildingOptions = buildingStore.buildings
    .sort((a, b) => (a.facility_id === 0) - (b.facility_id === 0) || (a.facility_id - b.facility_id))
    .map((building) => {
      return {
        value: building.asset_name,
        label: building.name,
      };
    });

  const [selectedAsset, setSelectedAsset] = useState({
    value: "",
    label: "Select Building",
  });
  const [selectedMonth, setSelectedMonth] = useState(
    moment().add("-1", "months").toDate()
  );
  const [loading, setLoading] = useState(false);

  // Form Fields

  // Recipient Details
  const [paymentNoticeNumber, setPaymentNoticeNumber] = useState(0);
  const [clientAsset, setClientAsset] = useState("");
  const [nickname, setNickname] = useState("");
  const [clientAddress1, setClientAddress1] = useState("");
  const [clientAddress2, setClientAddress2] = useState("");
  const [clientAddress3, setClientAddress3] = useState("");
  const [clientAddress4, setClientAddress4] = useState("");
  const [issueDate, setIssueDate] = useState(new Date());
  const [startBillingDate, setStartBillingDate] = useState(null);
  const [endBillingDate, setEndBillingDate] = useState(null);
  const [clientFacilityId, setClientFacilityId] = useState("");
  // Discharge Amount for Building
  const [showDischargeAmountBuilding, setShowDischargeAmountBuilding] =
    useState(false);
  const [dischargeAmountBuilding, setDischargeAmountBuilding] = useState(0);
  const [
    discountedElectricityRateBuilding,
    setDiscountedElectricityRateBuilding,
  ] = useState(0.1);
  // Auxiliary Power
  const [showAuxiliaryPower, setShowAuxiliaryPower] = useState(false);
  const [electricityBillInclusiveGST, setElectricityBillInclusiveGST] =
    useState(0);
  const [electricityBillExclusiveGST, setElectricityBillExclusiveGST] =
    useState(0);
  const [electricityRate, setElectricityRate] = useState(0);
  const [batteryAuxPowerConsumption, setBatteryAuxPowerConsumption] =
    useState(0);
  // Daily Rebates Table
  const [dailyRebates, setDailyRebates] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [cceAmount, setCceAmount] = useState(0);
  const [clientAmount, setClientAmount] = useState(0);
  // Monthly Graph
  const [monthlyRebates, setMonthlyRebates] = useState([]);
  const [totalSavings, setTotalSavings] = useState([]);
  // Payment Summary
  const [
    totalAccountReceivablesExclusiveGST,
    setTotalAccountReceivablesExclusiveGST,
  ] = useState(0);
  const [
    totalAccountReceivablesInclusiveGST,
    setTotalAccountReceivablesInclusiveGST,
  ] = useState(0);
  // Remarks Column
  const [remarks, setRemarks] = useState("Nil");

  const onChangeBillingDate = (dates) => {
    const [start, end] = dates;
    setStartBillingDate(start);
    setEndBillingDate(end);
  };
  const [lastSaved, setLastSaved] = useState("");

  async function handleGeneratePaymentNotice() {
    setLoading(true);
    try {
      const response = await axios.post(
        "/invoices/generateInvoice?selectedAsset=" +
          selectedAsset.value +
          "&selectedMonth=" +
          selectedMonth
      );
      if (response.status === 200) {
        setClientAsset(response.data.clientAsset);
        setMonthlyRebates(response.data.yearGraphByMonth);
        setTotalSavings(response.data.totalSavings);
        setPaymentNoticeNumber(Number(response.data.paymentNoticeNumber) + 1);
        setNickname(response.data.nickname);
        setClientAddress1(response.data.clientAddress1);
        setClientAddress2(response.data.clientAddress2);
        setClientAddress3(response.data.clientAddress3);
        setClientAddress4(response.data.clientAddress4);
        setClientFacilityId(response.data.clientFacilityId);
        setDischargeAmountBuilding(response.data.dischargeAmountBuilding);
        setBatteryAuxPowerConsumption(response.data.batteryAuxPowerConsumption);
        setStartBillingDate(new Date(response.data.startBillingPeriod));
        setEndBillingDate(new Date(response.data.endBillingPeriod));
        setElectricityRate(response.data.electricityRate);
        setElectricityBillInclusiveGST(response.data.electricityBillInclusiveGST);
        setElectricityBillExclusiveGST(response.data.electricityBillExclusiveGST);
        setDailyRebates(response.data.dailyRebates);
        setTotalAmount(response.data.totalAmount);
        setCceAmount(response.data.cceAmount);
        setClientAmount(response.data.clientAmount);
        setTotalAccountReceivablesExclusiveGST(
          response.data.totalAccountReceivablesExclusiveGST
        );
        setTotalAccountReceivablesInclusiveGST(
          response.data.totalAccountReceivablesInclusiveGST
        );
        if (response.data.defaultRemark) {
          setRemarks(response.data.defaultRemark);
        }
        getLastSavedPaymentNotice();
      }
    } finally {
      setLoading(false);
    }
  }

  async function getLastSavedPaymentNotice() {
    setLastSaved("");

    const response = await axios.post(
      "/invoices/lastSavedInvoice?selectedAsset=" +
          selectedAsset.value +
          "&selectedMonth=" +
          selectedMonth
    );

    if (response.status === 200 && response.data !== null) {
      setLastSaved(`Last saved: ${moment(response.data.createdAt).format("DD/MM/YYYY HH:mm:ss")}. Notice number: ${response.data.notice_number}.`);
    } else {
      setLastSaved("");
    }
  }

  async function handleConvertToPDF() {
    // handle pressed convert to PDF, this would allow the button to show
    setLoading(true);
    try {
      const svg = document.getElementById("hiddenBarChart");
      let pngData = await svgToPng(svg, 1200, 600);
      const response = await axios.post(
        "/invoices/generateInvoicePDF?selectedAsset=" +
          selectedAsset.value +
          "&selectedMonth=" +
          selectedMonth,
        {
          assetName: selectedAsset.value,
          // Recipient Details
          paymentNoticeNumber: paymentNoticeNumber,
          facilityId: clientFacilityId,
          nickname: nickname,
          clientAddress1: clientAddress1,
          clientAddress2: clientAddress2,
          clientAddress3: clientAddress3,
          clientAddress4: clientAddress4,
          issueDate: issueDate,
          startBillingDate: startBillingDate,
          endBillingDate: endBillingDate,
          clientFacilityId: clientFacilityId,
          // Discharge Amount for Buildings with Discounted Rate
          showDischargeAmountBuilding: showDischargeAmountBuilding,
          dischargeAmountBuilding: dischargeAmountBuilding,
          discountedElectricityRateBuilding: discountedElectricityRateBuilding,
          // Aux Power
          showAuxiliaryPower: showAuxiliaryPower,
          batteryAuxPowerConsumption: batteryAuxPowerConsumption,
          electricityRate: electricityRate,
          electricityBillExclusiveGST: electricityBillExclusiveGST,
          electricityBillInclusiveGST: electricityBillInclusiveGST,
          // Accounts Receivables
          totalAccountReceivablesExclusiveGST:
            totalAccountReceivablesExclusiveGST,
          totalAccountReceivablesInclusiveGST:
            totalAccountReceivablesInclusiveGST,
          dailyRebates: dailyRebates,
          totalAmount: totalAmount,
          cceAmount: cceAmount,
          clientAmount: clientAmount,
          // Remarks
          remarks: remarks,
          // png Data
          pngData: pngData,
          totalSavings: totalSavings,
        }
      );
      if (response.status === 200) {
        const doc = new jsPDF({
          unit: "mm",
          format: "a4",
          putOnlyUsedFonts: true,
          compress: true,
        });

        doc.addPage();
        doc.addPage();
        doc.html(response.data.htmlPage1, {
          callback: function (pdf) {
            pdf.html(response.data.htmlPage2, {
              callback: function (pdf2) {
                pdf2.html(response.data.htmlPage3, {
                  callback: function (pdf3) {
                    // Keep loading state until download starts
                    pdf3.save(
                      `${selectedAsset.label} - ${moment(selectedMonth).format(
                        "MMM YYYY"
                      )}`
                    );
                    // Set loading to false after download starts
                    setLoading(false);
                  },
                  x: 12.5,
                  y: 590,
                  width: 185,
                  windowWidth: 850,
                  margin: [0, 10, 10, 0],
                });
              },
              x: 12.5,
              y: 310,
              width: 185,
              windowWidth: 850,
              margin: [0, 0, 0, 0],
            });
          },
          x: 12.5,
          y: 10,
          width: 185,
          windowWidth: 850,
          margin: [0, 10, 10, 0],
        });
      }
    } catch (error) {
      setLoading(false);
    } finally {
      setPaymentNoticeNumber(paymentNoticeNumber+1);
      getLastSavedPaymentNotice();
    }
  }

  function onInputChange(e, setStateFunction) {
    setStateFunction(e.target.value);
  }

  function onInputChangeCheckbox(e, setStateFunction) {
    setStateFunction(e.target.checked);
  }

  return (
    <div className={styles.PaymentNoticeContainer}>
      <div className={styles.GenerateContainer}>
        <div className={styles.SelectContainer}>
          <div className={styles.AssetSelectContainer}>
            <div>Building:</div>
            <Select
              value={selectedAsset}
              options={buildingOptions}
              onChange={setSelectedAsset}
              classNamePrefix="react-select"
              styles={{
                control: (b) => ({ ...b, backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)' }),
                singleValue: (b) => ({ ...b, color: 'var(--body-color)' }),
                menu: (b) => ({ ...b, backgroundColor: 'var(--card-bg)', color: 'var(--body-color)' }),
                option: (b, s) => ({ ...b, backgroundColor: s.isFocused ? 'var(--sidebar-hover-bg)' : 'transparent', color: 'var(--body-color)' })
              }}
            />
          </div>
          <div className={styles.MonthSelectContainer}>
            <div>Month:</div>
            <DatePicker
              dateFormat="MM/yyyy"
              selected={selectedMonth}
              onChange={(date) => setSelectedMonth(date)}
              showMonthYearPicker
              maxDate={moment().add("-2", "weeks").toDate()}
            />
          </div>
        </div>
        <div className={styles.BottomContainer}>
          <Button 
            color="primary" 
            onClick={handleGeneratePaymentNotice}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Generating...
              </>
            ) : (
              "Generate Payment Notice"
            )}
          </Button>
        </div>
      </div>

      {paymentNoticeNumber !== 0 && (
        <div className={styles.FormInputContainer}>
          <h4 className={styles.FormHeader}>Recipient's Details</h4>
          <Form className={styles.Form}>
            <FormGroup className={styles.FormGroup}>
              <div className={styles.FormInputContainer}>
                <Label>Payment Notice Number</Label>
                <Input
                  type="text"
                  value={paymentNoticeNumber}
                  onChange={(e) => onInputChange(e, setPaymentNoticeNumber)}
                  disabled
                />
              </div>
              <div className={styles.FormInputContainer}>
                {lastSaved && <div style={{ marginTop: "auto" }}>{lastSaved}</div>}
              </div>
              <div className={styles.FormInputContainer}>
                <Label>Client Address 1</Label>
                <Input
                  type="text"
                  value={clientAddress1}
                  onChange={(e) => onInputChange(e, setClientAddress1)}
                />
              </div>
              <div className={styles.FormInputContainer}>
                <Label>Client Address 2</Label>
                <Input
                  type="text"
                  value={clientAddress2}
                  onChange={(e) => onInputChange(e, setClientAddress2)}
                />
              </div>
              <div className={styles.FormInputContainer}>
                <Label>Client Address 3</Label>
                <Input
                  type="text"
                  value={clientAddress3}
                  onChange={(e) => onInputChange(e, setClientAddress3)}
                />
              </div>
              <div className={styles.FormInputContainer}>
                <Label>Client Address 4</Label>
                <Input
                  type="text"
                  value={clientAddress4}
                  onChange={(e) => onInputChange(e, setClientAddress4)}
                />
              </div>
              <div className={styles.FormInputContainer}>
                <Label>Billing Period</Label>
                <div>
                  <DatePicker
                    selected={startBillingDate}
                    onChange={onChangeBillingDate}
                    startDate={startBillingDate}
                    endDate={endBillingDate}
                    selectsRange
                    inline
                  />
                </div>
              </div>
              <div className={styles.FormInputContainer}>
                <Label>Issue Date</Label>
                <div>
                  <DatePicker
                    selected={issueDate}
                    onChange={(date) => setIssueDate(date)}
                    inline
                  />
                </div>
              </div>
              <div className={styles.FormInputContainer}>
                <Label>Facility ID</Label>
                <Input
                  type="text"
                  value={clientFacilityId}
                  onChange={(e) => onInputChange(e, setClientFacilityId)}
                />
              </div>
            </FormGroup>
          </Form>

          <>
            <h4 className={styles.FormHeader}>Discharge Amount</h4>
            <div className={styles.CheckboxContainer}>
              <Input
                type="checkbox"
                value={showDischargeAmountBuilding}
                onChange={(e) =>
                  onInputChangeCheckbox(e, setShowDischargeAmountBuilding)
                }
              />
              <Label check>Show Discharge Amount</Label>
            </div>
            {showDischargeAmountBuilding && (
              <DischargeFormComponent
                onInputChange={onInputChange}
                dischargeAmountBuilding={dischargeAmountBuilding}
                setDischargeAmountBuilding={setDischargeAmountBuilding}
                discountedElectricityRateBuilding={
                  discountedElectricityRateBuilding
                }
                setDiscountedElectricityRateBuilding={
                  setDiscountedElectricityRateBuilding
                }
              />
            )}
          </>

          <h4 className={styles.FormHeader}>Auxiliary Power</h4>
          <div className={styles.CheckboxContainer}>
            <Input
              type="checkbox"
              value={showAuxiliaryPower}
              onChange={(e) => onInputChangeCheckbox(e, setShowAuxiliaryPower)}
            />
            <Label check>Show Auxiliary Power Consumption</Label>
          </div>
          {showAuxiliaryPower && (
            <Form className={styles.Form}>
              <FormGroup className={styles.FormGroup}>
                <div className={styles.FormInputContainer}>
                  <Label>Battery Auxiliary Power Consumption (kWh)</Label>
                  <Input
                    type="number"
                    value={batteryAuxPowerConsumption}
                    onChange={(e) =>
                      onInputChange(e, setBatteryAuxPowerConsumption)
                    }
                    step={0.001}
                  />
                </div>
                <div className={styles.FormInputContainer}>
                  <Label>Electricity Rate ($/kWh)</Label>
                  <Input
                    type="number"
                    value={electricityRate}
                    onChange={(e) => onInputChange(e, setElectricityRate)}
                    step={0.01}
                  />
                </div>
                <div className={styles.FormInputContainer}>
                  <Label>Electricity Bill in SGD (Exclusive of 9% GST)</Label>
                  <Input
                    type="number"
                    value={electricityBillExclusiveGST}
                    onChange={(e) =>
                      onInputChange(e, setElectricityBillExclusiveGST)
                    }
                    step={0.01}
                  />
                </div>
                <div className={styles.FormInputContainer}>
                  <Label>Electricity Bill in SGD (Inclusive of 9% GST)</Label>
                  <Input
                    type="number"
                    value={electricityBillInclusiveGST}
                    onChange={(e) =>
                      onInputChange(e, setElectricityBillInclusiveGST)
                    }
                    step={0.01}
                  />
                </div>
              </FormGroup>
            </Form>
          )}
          <h4 className={styles.FormHeader}>Accounts Receivables</h4>
          <Form className={styles.Form}>
            <FormGroup className={styles.FormGroup}>
              <div className={styles.FormInputContainer}>
                <Label>Account Receivables (Exclusive of 9% GST)</Label>
                <Input
                  type="number"
                  value={totalAccountReceivablesExclusiveGST}
                  onChange={(e) =>
                    onInputChange(e, setTotalAccountReceivablesExclusiveGST)
                  }
                  step={0.01}
                />
              </div>
              <div className={styles.FormInputContainer}>
                <Label>Account Receivables (Inclusive of 9% GST)</Label>
                <Input
                  type="number"
                  value={totalAccountReceivablesInclusiveGST}
                  onChange={(e) =>
                    onInputChange(e, setTotalAccountReceivablesInclusiveGST)
                  }
                  step={0.01}
                />
              </div>
            </FormGroup>
          </Form>
          <h4 className={styles.FormHeader}>Daily Summary</h4>
          <div className={styles.Form}>
            <DailySummaryTable
              dailyRebates={dailyRebates}
              setDailyRebates={setDailyRebates}
              totalAmount={totalAmount}
              setTotalAmount={setTotalAmount}
              cceAmount={cceAmount}
              setCceAmount={setCceAmount}
              clientAmount={clientAmount}
              setClientAmount={setClientAmount}
              onInputChange={onInputChange}
            />
          </div>
          <h4 className={styles.FormHeader}>Remarks</h4>
          <Input
            type="text"
            value={remarks}
            onChange={(e) => onInputChange(e, setRemarks)}
          />
          <div className={styles.ConvertButtonContainer}>
            <Button onClick={handleConvertToPDF}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Printing...
                </>
              ) : (
                "Save & Print"
              )}
            </Button>
          </div>
        </div>
      )}

      <div>
        <div className={styles.hiddenGraph}>
          <BarChart
            id="hiddenBarChart"
            width={1200}
            height={600}
            data={monthlyRebates}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis
              type="number"
              domain={([dataMax, dataMin]) => {
                const highest = Math.max(Number(dataMax), Number(dataMin));
                const absMax = Math.ceil(Number(highest) / 100) * 100 + 100;
                return [0, absMax];
              }}
            />

            <Tooltip />
            <Legend />
            <Bar
              dataKey="value"
              fill="#8884d8"
              activeBar={<Rectangle fill="pink" stroke="blue" />}
            >
              <LabelList dataKey="value" position="top" />
            </Bar>
          </BarChart>
        </div>
      </div>
    </div>
  );
}
export const svgToPng = (svg, width, height) => {
  return new Promise((resolve, reject) => {
    let canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    let ctx = canvas.getContext("2d");

    // Set background to white
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);

    let xml = new XMLSerializer().serializeToString(svg);
    let dataUrl = "data:image/svg+xml;utf8," + encodeURIComponent(xml);
    let img = new Image(width, height);

    img.onload = () => {
      ctx.drawImage(img, 0, 0);
      let imageData = canvas.toDataURL("image/png", 1.0);
      resolve(imageData);
    };

    img.onerror = () => reject();

    img.src = dataUrl;
    return dataUrl;
  });
};
